import type { SequenceTriggerType } from "@unbox/api";
import { AlertTriangle, Play } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useCreateSequence, useSequence, useUpdateSequence } from "@/api/sequences";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { EXIT_SCENARIOS, TRIGGER_EVENT_LABELS, TRIGGER_LABELS } from "./sequence-steps-preview";
import { collapseForPersist, expandPersistedSteps, type Step, StepBuilder } from "./step-builder";

function validateSteps(steps: Step[]): string | null {
  if (steps.length === 0) {
    return "Add at least one step before saving.";
  }
  for (const s of steps) {
    if (s.type === "send_message" && !s.messageTemplateId && !s.customMessage) {
      return `Step ${s.step + 1} is a Send Message step with no message. Please add a template or custom message.`;
    }
  }
  return null;
}

interface SequenceEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerType: SequenceTriggerType;
  /** e.g. "Global" or the collaboration name. Used in the dialog title and to auto-name new sequences. */
  scope: string;
  /** ID of the sequence to edit. When null, saving creates a new sequence. */
  sequenceId: string | null;
  /** When `sequenceId` is null, seed the step editor with this sequence's steps (e.g. the global's steps when creating a new override). */
  seedFromSequenceId?: string | null;
  /** Render an inline checkbox asking whether to apply this default to existing collaborations.
   *  Only meaningful when setting a global default for the first time. The checkbox state is
   *  returned via `onSaved(…, applyToExisting)`. Default: not shown. */
  showApplyToExistingOption?: boolean;
  /** Called after a successful save. Receives the sequence id, whether it was newly created,
   *  and (if the option was shown) the checkbox state. */
  onSaved?: (sequenceId: string, created: boolean, applyToExisting: boolean) => void;
}

export function SequenceEditorDialog({
  open,
  onOpenChange,
  triggerType,
  scope,
  sequenceId,
  seedFromSequenceId,
  showApplyToExistingOption,
  onSaved,
}: SequenceEditorDialogProps) {
  const isEditing = !!sequenceId;
  const loadId = sequenceId ?? seedFromSequenceId ?? null;

  const { data: loadedSequence } = useSequence(loadId ?? "", { enabled: !!loadId });
  const createSequence = useCreateSequence();
  const updateSequence = useUpdateSequence(sequenceId ?? "");

  const [steps, setSteps] = useState<Step[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [applyToExisting, setApplyToExisting] = useState(false);

  // Sync steps whenever the dialog (re-)opens or the loaded sequence changes.
  useEffect(() => {
    if (!open) return;
    if (loadedSequence) {
      setSteps(expandPersistedSteps(loadedSequence.steps));
      setIsDirty(false);
      setApplyToExisting(false);
    } else {
      // Either no source to load from, or we're still waiting for one — start empty so
      // we never show stale steps from a previous sequence while the new one hydrates.
      setSteps([]);
      setIsDirty(false);
      setApplyToExisting(false);
    }
  }, [open, loadedSequence, loadId]);

  const handleStepsChange = (next: Step[]) => {
    setSteps(next);
    setIsDirty(true);
  };

  const type = triggerType === "safety_action" ? "safety" : "creator";
  const triggerLabel = TRIGGER_LABELS[triggerType] ?? triggerType;
  const triggerEventLabel = TRIGGER_EVENT_LABELS[triggerType] ?? triggerLabel;
  const title = `${triggerLabel} - ${scope}`;

  const handleSave = async () => {
    const stepError = validateSteps(steps);
    if (stepError) {
      toastError(stepError);
      return;
    }
    try {
      if (isEditing) {
        await updateSequence.mutateAsync({ steps: collapseForPersist(steps) });
        toastSuccess("Sequence updated");
        onSaved?.(sequenceId, false, false);
      } else {
        const created = await createSequence.mutateAsync({
          name: `${triggerLabel} - ${scope}`,
          type,
          steps: collapseForPersist(steps),
        });
        toastSuccess("Sequence saved");
        onSaved?.(created.id, true, showApplyToExistingOption ? applyToExisting : false);
      }
      onOpenChange(false);
    } catch {
      toastError(isEditing ? "Failed to update sequence" : "Failed to save sequence");
    }
  };

  const isSaving = createSequence.isPending || updateSequence.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Updating will apply to new runs. Active runs keep their current version."
                : "Add the steps that should run when this trigger fires."}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg bg-secondary p-4 max-h-[55vh] overflow-y-auto">
            <div className="sequence-steps relative flex flex-col">
              <div className="flex items-start gap-3 pb-2">
                <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 text-white shrink-0 mt-2.5">
                  <Play className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col flex-1 rounded-lg border border-gray-200 bg-primary shadow-sm px-4 py-3 gap-2">
                  <span className="text-sm font-medium text-secondary">{triggerEventLabel}</span>
                  {EXIT_SCENARIOS[triggerType] && (
                    <div className="rounded-md bg-gray-100 px-3 py-2">
                      <p className="text-xs font-medium text-secondary">Exits if</p>
                      <ul className="text-xs text-tertiary space-y-1 list-disc pl-4 mt-1">
                        {EXIT_SCENARIOS[triggerType].map((scenario) => (
                          <li key={scenario}>{scenario}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <StepBuilder
                steps={steps}
                onStepsChange={handleStepsChange}
                emptyMessage="No steps yet. Add a step to get started."
              />
            </div>
          </div>

          {isEditing && isDirty && (
            <div className="flex items-start gap-2.5 rounded-md bg-utility-warning-50 border border-utility-warning-200 px-3 py-2.5">
              <AlertTriangle className="w-4 h-4 text-utility-warning-600 shrink-0 mt-0.5" />
              <p className="text-xs text-utility-warning-700">
                Changes only apply to new sequence runs. Runs already in progress keep their current configuration.
              </p>
            </div>
          )}

          {showApplyToExistingOption && !isEditing && (
            <div className="flex flex-col gap-2 rounded-md bg-secondary px-3 py-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToExisting}
                  onChange={(e) => setApplyToExisting(e.target.checked)}
                  className="mt-0.5 size-4 shrink-0 rounded border-gray-300 text-brand-solid focus:ring-brand"
                />
                <span className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-primary">Apply to existing collaborations</span>
                  <span className="text-xs text-tertiary">
                    Enable this trigger on all existing collaborations, unless they already have a custom automation
                    set.
                  </span>
                </span>
              </label>
              <p className="text-xs text-quaternary pl-6">
                This default will automatically apply to any new TikTok collaborations ingested by the system.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button color="primary" size="sm" isLoading={isSaving} onClick={handleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
